import { FolderIcon, PlusIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { PageContainer } from "@/layouts/AppLayout";
import { Button, EmptyState, ErrorState, Field, PageSpinner } from "@/components/ui";
import { Modal } from "@/components/ui/Modal";
import { api, errorMessage } from "@/lib/api";
import { keys, useAction, useMe, useProjects } from "@/lib/queries";
import { pluralize } from "@/lib/utils";

function suggestKey(name: string) {
  const words = name.toUpperCase().replace(/[^A-Z0-9 ]/g, "").split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const key = words.length > 1 ? words.map((w) => w[0]).join("") : words[0];
  return /^[A-Z]/.test(key) ? key.slice(0, 5) : `P${key}`.slice(0, 5);
}

function CreateProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const create = useAction(() => api.createProject({ name, key: keyTouched ? key : suggestKey(name), description }), {
    invalidate: [keys.projects],
    onSuccess: (p) => {
      onClose();
      navigate(`/p/${p.id}`);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const effectiveKey = keyTouched ? key : suggestKey(name);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!name.trim() || effectiveKey.length < 2} onClick={() => create.mutate()}>
            Create project
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Project name">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className="field" placeholder="University Platform" dir="auto" />
        </Field>
        <Field label="Key" hint="Short prefix for task numbers, e.g. UNI-42">
          <input
            value={effectiveKey}
            onChange={(e) => {
              setKeyTouched(true);
              setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10));
            }}
            className="field font-mono uppercase"
          />
        </Field>
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="field resize-none" dir="auto" />
        </Field>
        <p className="text-xs text-faint">New projects start with the default stages (Backlog → Done), sprint statuses, and epic & task properties. You can change all of them later.</p>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}

export function ProjectsPage() {
  const projects = useProjects();
  const me = useMe().data!;
  const [creating, setCreating] = useState(false);

  return (
    <PageContainer>
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="mt-1 text-sm text-muted">{me.isAdmin ? "All projects in the workspace" : "Projects you're a member of"}</p>
        </div>
        <Button variant="primary" icon={PlusIcon} onClick={() => setCreating(true)}>
          New project
        </Button>
      </div>
      {projects.isPending && <PageSpinner />}
      {projects.error && <ErrorState message={errorMessage(projects.error)} onRetry={() => projects.refetch()} />}
      {projects.data?.length === 0 && (
        <EmptyState
          icon={FolderIcon}
          title="No projects yet"
          action={
            <Button variant="primary" icon={PlusIcon} onClick={() => setCreating(true)}>
              Create your first project
            </Button>
          }
        >
          Create a project, or ask a project owner to add you to one.
        </EmptyState>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {projects.data?.map((p) => (
          <Link key={p.id} to={`/p/${p.id}`} className="group rounded-xl border border-line p-4 transition-colors hover:bg-hover">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-xs font-bold text-white">{p.key.slice(0, 3)}</span>
              <div className="min-w-0">
                <div className="truncate font-semibold" dir="auto">
                  {p.name}
                </div>
                <div className="text-xs text-faint capitalize">{p.role}</div>
              </div>
            </div>
            {p.description && (
              <p className="mt-3 line-clamp-2 text-sm text-muted" dir="auto">
                {p.description}
              </p>
            )}
            <div className="mt-3 flex gap-3 text-xs text-muted">
              <span>{pluralize(p.epicCount, "epic")}</span>
              <span>{pluralize(p.taskCount, "task")}</span>
              <span>{pluralize(p.memberCount, "member")}</span>
            </div>
          </Link>
        ))}
      </div>
      <CreateProjectDialog open={creating} onClose={() => setCreating(false)} key={String(creating)} />
    </PageContainer>
  );
}
